import { useEffect, useState } from 'react';
import OverlayPanel from '../common/overlay-panel/overlay-panel.component';
import type { OverlayScrollDetectionType } from '../common/overlay-panel/overlay-panel.props';
import type { DomElementAlignment } from '../enums';
import { DomTargetPosition } from '../enums';
import { isDropDownNoSelection } from '../fields/drop-down/drop-down-common';
import type { DropDownNoSelection, DropDownResult } from '../fields/drop-down/drop-down-types';
import DateRangeFilterField from '../data-grid/filters/date-range-filter-field.component';
import { getCustomDateRangeItem } from './date-range-filter-functions';

export interface DateRange {
  from?: Date;
  to?: Date;
}

interface DateRangeFilterProps {
  value?: DateRange;
  visible: boolean;
  onApplyFilter: (dateRange: DateRange, quickDateRange?: DropDownResult) => void;
  onClearFilter: () => void;
  target?: React.MouseEvent<Element, MouseEvent> | HTMLElement | Element | null;
  alignment?: DomElementAlignment;
  position?: DomTargetPosition;
  scrollDetection?: OverlayScrollDetectionType;
}

const DateRangeFilter = ({
  value,
  visible,
  onApplyFilter,
  onClearFilter,
  target,
  alignment,
  position = DomTargetPosition.BottomLeft,
  scrollDetection,
}: DateRangeFilterProps) => {
  const [quickDateRange, setQuickDateRange] = useState<DropDownResult | DropDownNoSelection>();
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();

  useEffect(() => {
    if (value) {
      if (!fromDate && !toDate && !quickDateRange) {
        setFromDate(value.from);
        setToDate(value.to);
        setQuickDateRange(getCustomDateRangeItem());
      }
    } else {
      clearFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only sync when value prop changes, check state to avoid overwriting user changes
  }, [value]);

  const clearFilter = () => {
    setFromDate(undefined);
    setToDate(undefined);
    setQuickDateRange(undefined);
  };

  const handleHidden = () => {
    if ((fromDate || toDate) && !isDropDownNoSelection(quickDateRange)) {
      onApplyFilter({ from: fromDate, to: toDate }, quickDateRange);
    } else {
      onClearFilter();
    }
  };

  return (
    <OverlayPanel
      visible={visible}
      target={target}
      targetPosition={position}
      elementAlignment={alignment}
      scrollDetection={scrollDetection}
      hidden={handleHidden}
      shouldScrollCloseOverlay
      shouldRemainOnScreen
      shouldCheckZIndex
      className="odin-w-[400px] odin-min-w-min odin-max-w-lg odin-p-4 odin-mt-2 odin-bg-mono-1 odin-shadow-lg odin-border odin-border-gray-4 odin-rounded-md odin-flex odin-gap-y-4 odin-flex-col"
    >
      <DateRangeFilterField
        fromDate={fromDate}
        toDate={toDate}
        quickDateRange={quickDateRange}
        setFromDate={setFromDate}
        setToDate={setToDate}
        setQuickDateRange={setQuickDateRange}
        clearFilter={clearFilter}
      />
    </OverlayPanel>
  );
};

export default DateRangeFilter;
